import type {ControlOption} from 'shells/card/segmented-control';

import type {
    ChatAssignmentStrategyEnum,
    ChatVariantEnum,
    ChatbotToneEnum,
    ChatbotResponseTimingEnum,
    ChatbotAnswerLengthEnum,
    ChatbotIconEnum,
    NudgeDeviceVisibilityEnum,
} from 'nutshell-graphql-types';

export const SAVE_DELAY = 1000;
export const OFFLINE_MESSAGE_LIMIT = 200;
export const NUDGE_MESSAGE_LIMIT = 50;
export const NAME_DISPLAY_LIMIT = 25;
export const CONVERSATION_STARTER_LIMIT = 200;
export const DISCLAIMER_CHARACTER_LIMIT = 200;
export const DISABLED_PATHS_LIMIT = 20;

export const SAVE_STATE_KEYS = {
    COLOR: 'color',
    SECONDARY_COLOR: 'secondary-color',
    ICON: 'icon',
    NAME: 'name',
    SMART_NUDGES: 'smart-nudges',
    OPENING_MESSAGE: 'opening-message',
    NUDGE: 'nudge',
    ASSIGNMENT: 'assignment',
    CHATBOT_NAME: 'chatbot-name',
    CHATBOT_PREVIEW_WEBSITE_URL: 'chatbot-preview-website-url',
    CHATBOT_SUGGESTIONS: 'chatbot-suggestions',
    CHATBOT_TOPICS_TO_AVOID: 'chatbot-topics-to-avoid',
    OFFLINE_MESSAGE: 'offline-message',
    OFFLINE_TYPE: 'offline-type',
    FORM_TYPE: 'form-type',
    LEAD_TRACKING: 'lead-tracking',
    FORM_FIELDS: 'form-fields',
    RESPONSE_TIMING: 'response-timing',
    TONE: 'tone',
    ANSWER_LENGTH: 'answer-length',
    SUGGESTIONS: 'suggestions',
    TOPICS_TO_AVOID: 'topics-to-avoid',
    GOALS_SCHEDULER: 'goals-scheduler',
    GOALS_FORM: 'goals-form',
    SHOW_POWERED_BY: 'show-powered-by',
    DISCLAIMER: 'disclaimer',
    ACTION_BAR_ACTIONS: 'action-bar-actions',
    CHATBOT_ICON: 'chatbot-icon',
    CONVERSATION_STARTER: 'conversation-starter',
    ENGAGEMENT_BAR: 'engagement-bar',
    CALL_TO_ACTION: 'call-to-action',
    ALLOW_THREADS_WITHOUT_CONTACT: 'allow-threads-without-contact',
    DISABLED_PATHS: 'disabled-paths',
} as const;

export type SaveStateKey = (typeof SAVE_STATE_KEYS)[keyof typeof SAVE_STATE_KEYS];

export const ASSIGNMENT_OPTIONS: ControlOption<ChatAssignmentStrategyEnum>[] = [
    {
        type: 'small',
        title: 'Round-robin',
        value: 'ROUND_ROBIN' as ChatAssignmentStrategyEnum,
    },
    {
        type: 'small',
        title: 'Specific-user',
        value: 'SPECIFIC_USER' as ChatAssignmentStrategyEnum,
    },
    {
        type: 'small',
        title: 'Nobody',
        value: 'NOBODY' as ChatAssignmentStrategyEnum,
    },
];

export const CHAT_VARIANT = {
    CHAT: 'CHAT' as ChatVariantEnum,
    ACTIONS: 'ACTIONS' as ChatVariantEnum,
} as const;

export const CHATBOT_TONE_OPTIONS: ControlOption<ChatbotToneEnum>[] = [
    {type: 'small', title: 'Neutral', value: 'NEUTRAL'},
    {type: 'small', title: 'Friendly', value: 'FRIENDLY'},
    {type: 'small', title: 'Professional', value: 'PROFESSIONAL'},
];

export const CHATBOT_RESPONSE_TIMING_OPTIONS: ControlOption<ChatbotResponseTimingEnum>[] = [
    {type: 'small', title: 'Always', value: 'ALWAYS'},
    {type: 'small', title: 'Team is offline', value: 'OFFLINE'},
    {type: 'small', title: 'Never', value: 'NEVER'},
];

export const CHATBOT_ANSWER_LENGTH_OPTIONS: ControlOption<ChatbotAnswerLengthEnum>[] = [
    {type: 'small', title: 'Concise', value: 'CONCISE'},
    {type: 'small', title: 'Normal', value: 'NORMAL'},
    {type: 'small', title: 'Complete', value: 'COMPLETE'},
];

export const CHATBOT_ICON_OPTIONS: ControlOption<ChatbotIconEnum>[] = [
    {type: 'small', title: 'Chatbot', value: 'CHATBOT'},
    {type: 'small', title: 'Sparkle', value: 'SPARKLE'},
    {type: 'small', title: 'None', value: 'NONE'},
];

export const NUDGE_DEVICE_VISIBILITY_OPTIONS: ControlOption<NudgeDeviceVisibilityEnum>[] = [
    {type: 'small', title: 'Only on desktop', value: 'DESKTOP_ONLY'},
    {type: 'small', title: 'Always', value: 'ALWAYS'},
];

export const FORM_TYPE_REGULAR = 'REGULAR';
export const FORM_TYPE_CONVERSATIONAL = 'CONVERSATIONAL';
export const FORM_TYPE_NONE = 'NONE';
